import { WSList } from "@wingspanhq/fe-component-library";
import {
  BiometricIdentityVerificationRequest,
  BiometricIdentityVerificationRequestStatus,
  SharedFileRequestJobStatus
} from "@wingspanhq/wingspan-api-sdk";

import { WayfinderWidget } from "../../../../../../shared/components/WayfinderWidget";
import {
  IDENTITY_VERIFICATION_CHECKS,
  IDENTITY_VERIFICATION_FAILURE_RISK_SIGNALS
} from "../../../../../RequirementDefinitions/modules/IdentityVerification/constants";
import { AIValidationWidget } from "../../../../components/AIValidationWidget";
import { ValidationItemProps } from "../../../../components/AIValidationWidget/ValidationItem";
import {
  RequirementContextType,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";

interface Props {
  identityVerification: BiometricIdentityVerificationRequest;
  contextType: RequirementContextType;
  requirement: RequirementTableItemData;
  onSuccess: RequirementTableCallback;
}

/** The copy for every risk signal of a check that the verification raised. */
const aggregateSignalCopy = (
  checkRiskSignals: Record<string, string>,
  raisedSignals: string[] | undefined,
  isFailure: boolean
): string =>
  Object.keys(checkRiskSignals)
    .filter(
      signal =>
        raisedSignals?.includes(signal) &&
        IDENTITY_VERIFICATION_FAILURE_RISK_SIGNALS.includes(signal) ===
          isFailure
    )
    .map(signal => checkRiskSignals[signal])
    .join("\n\n");

export const IdentityVerificationDetailsWidgetsContainer: React.FC<Props> = ({
  identityVerification,
  contextType
}) => {
  // The signals live on the verification, which reaches a payer as the
  // request's embed once the request is terminal (and is absent before that).
  const riskSignals =
    identityVerification.biometricIdentityVerification?.riskSignals;

  // go over all possible checks
  const validationItems: ValidationItemProps[] =
    IDENTITY_VERIFICATION_CHECKS.map(checkFixture => {
      const aggregatedErrors = aggregateSignalCopy(
        checkFixture.riskSignals,
        riskSignals,
        true
      );
      const aggregatedWarnings = aggregateSignalCopy(
        checkFixture.riskSignals,
        riskSignals,
        false
      );

      const hasErrors = aggregatedErrors.length > 0;
      const hasWarnings = aggregatedWarnings.length > 0;

      let status: SharedFileRequestJobStatus;

      if (
        identityVerification.status ===
        BiometricIdentityVerificationRequestStatus.COMPLETE
      ) {
        status = SharedFileRequestJobStatus.COMPLETE;
      } else if (
        identityVerification.status ===
        BiometricIdentityVerificationRequestStatus.FAILED
      ) {
        status = hasErrors
          ? SharedFileRequestJobStatus.FAILED
          : SharedFileRequestJobStatus.COMPLETE;
      } else {
        // The request has only three statuses (Pending/Complete/Failed), so
        // anything not terminal is awaiting the contractor. A separate
        // "processing" branch used to sit here behind an `isInitialLoading` prop
        // that every call site hardcoded to `false` — it could never fire, and
        // the in-flight affordance is now the section shimmer in
        // `RequirementDetailsContent`.
        status = SharedFileRequestJobStatus.PENDING;
      }

      return {
        title: checkFixture.title,
        description: checkFixture.description,
        type: checkFixture.type,
        status,
        statusReason:
          status === SharedFileRequestJobStatus.FAILED
            ? aggregatedErrors
            : undefined,
        statusSummary:
          status === SharedFileRequestJobStatus.FAILED
            ? checkFixture.errorSummary
            : status === SharedFileRequestJobStatus.COMPLETE
              ? hasWarnings
                ? checkFixture.successWithWarningsSummary
                : checkFixture.successSummary
              : undefined,
        warningReason: aggregatedWarnings
      };
    });

  return (
    <WSList gap="L">
      <AIValidationWidget
        contextType={contextType}
        validationItems={validationItems}
      />
      {/* Show WayFinder widget only for payers */}
      {contextType === RequirementContextType.Payee && <WayfinderWidget />}
    </WSList>
  );
};
